import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useIsMobile } from '../../../hooks';
import { MobileList } from '../../../components/common';
import api from '../../../services/api';
import { grey, success } from '../../../styles/tokens';

/**
 * Liste des Clients (Coach)
 */
const ClientList = () => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [search, setSearch] = useState('');

  // Menu actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get('/coach/clients');
      setClients(data.clients || data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des clients:', err);
      setError('Impossible de charger la liste des clients.');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, client) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedClient) return;

    try {
      setActionLoading(true);
      await api.delete(`/coach/clients/${selectedClient._id}`);
      setDeleteDialog(false);
      handleMenuClose();
      fetchClients();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Impossible de supprimer le client.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChipProps = (status) => {
    return status === 'active'
      ? { label: 'Actif', sx: { bgcolor: success[50], color: success.dark } }
      : { label: 'Inactif', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  // Toggle statut client
  const handleToggleStatus = async (clientId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/coach/clients/${clientId}`, { status: newStatus });
      setClients(prev => prev.map(c =>
        c._id === clientId ? { ...c, status: newStatus } : c
      ));
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
      setError('Erreur lors du changement de statut.');
    }
  };

  // Filtrage par recherche
  const filteredClients = clients.filter((client) =>
    `${client.firstName} ${client.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <Helmet>
        <title>Clients - Coach Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h1">Clients</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            component={Link}
            to="/coach/clients/create"
          >
            Ajouter
          </Button>
        </Stack>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              placeholder="Rechercher par nom ou email..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              sx={{ maxWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: grey[400] }} />
                  </InputAdornment>
                ),
              }}
            />
          </CardContent>
        </Card>

        {/* Erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Liste */}
        <Card className={isMobile ? 'mobile-list-container' : ''}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredClients.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2">
                Aucun client trouve.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile: Liste en cards */
            <Box sx={{ p: 2 }}>
              <MobileList
                items={filteredClients}
                renderItem={(client) => {
                  const statusProps = getStatusChipProps(client.status);
                  return {
                    primary: `${client.firstName} ${client.lastName}`,
                    secondary: client.email,
                    meta: (
                      <Switch
                        size="small"
                        checked={client.status === 'active'}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleStatus(client._id, client.status);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        color="success"
                      />
                    ),
                    chips: [{ label: statusProps.label, sx: statusProps.sx }],
                    onClick: () => navigate(`/coach/clients/view/${client._id}`),
                  };
                }}
                emptyMessage="Aucun client trouve"
              />
            </Box>
          ) : (
            /* Desktop: Table classique */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Actif</TableCell>
                    <TableCell>Inscription</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow key={client._id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {client.firstName} {client.lastName}
                          </Typography>
                          <Typography variant="caption">
                            {client.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={client.status === 'active'}
                          onChange={() => handleToggleStatus(client._id, client.status)}
                          color="success"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {new Date(client.createdAt).toLocaleDateString('fr-FR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, client)}
                        >
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Menu actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            component={Link}
            to={`/coach/clients/view/${selectedClient?._id}`}
            onClick={handleMenuClose}
          >
            <VisibilityIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Voir details
          </MenuItem>

          <MenuItem
            component={Link}
            to={`/coach/clients/edit/${selectedClient?._id}`}
            onClick={handleMenuClose}
          >
            <EditIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Modifier
          </MenuItem>

          <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Supprimer
          </MenuItem>
        </Menu>

        {/* Dialog Suppression */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Supprimer le client ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir supprimer definitivement <strong>{selectedClient?.firstName} {selectedClient?.lastName}</strong> ?
              <br /><br />
              <strong>Cette action est irreversible.</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default ClientList;
